import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbInfoCircle, TbAlertCircle } from "react-icons/tb";
import { SettingRow, TextInput, Section } from "./SettingsControls";
import { normalizeIban, validateIban } from "src/helpers/sanitizeInput";
import { lookupBicFromIban } from "src/services/bicLookup";

type BankFields = {
    iban: string;
    bic: string;
    taxNumber: string;
    scanPayName: string;
};

interface BankSettingsProps {
    bank: BankFields;
    saved: boolean;
    dirty?: boolean;
    onChange: (b: BankFields) => void;
    onSave: () => void;
    onIbanError?: (hasError: boolean) => void;
    description?: string;
    fallbackIban?: string;
    fallbackBic?: string;
}

export function BankSettings({ bank, saved, dirty, onChange, onSave, onIbanError, description, fallbackIban, fallbackBic }: BankSettingsProps) {
    const { t } = useTranslation();
    const [ibanError, setIbanError] = useState<string | null>(null);
    const [bicLookingUp, setBicLookingUp] = useState(false);
    const bankRef = useRef(bank);
    useEffect(() => { bankRef.current = bank; }, [bank]);

    const handleIbanBlur = async (raw: string) => {
        const normalized = normalizeIban(raw);
        onChange({ ...bank, iban: normalized });
        const hasError = !!normalized && !validateIban(normalized);
        setIbanError(hasError ? t("register.errorIbanFormat") : null);
        onIbanError?.(hasError);

        if (!hasError && normalized && !bank.bic.trim()) {
            setBicLookingUp(true);
            const bic = await lookupBicFromIban(normalized);
            setBicLookingUp(false);
            if (bic && !bankRef.current.bic.trim()) {
                onChange({ ...bankRef.current, iban: normalized, bic });
            }
        }
    };

    const ibanEmpty = !bank.iban.trim();
    const showFallback = ibanEmpty && (fallbackIban || fallbackBic);

    return (
        <Section title={t("settings.bankDetails")} description={description}>
            {ibanEmpty && (
                <div className={`settings-info-banner settings-info-banner--neutral${showFallback ? " settings-info-banner--multiline" : ""}`}>
                    <TbInfoCircle />
                    <span>
                        {t("settings.ibanFallbackNotice")}
                        {showFallback && (
                            <span className="settings-info-banner__fallback">
                                {fallbackIban}{fallbackIban && fallbackBic ? " · " : ""}{fallbackBic}
                            </span>
                        )}
                    </span>
                </div>
            )}
            <SettingRow title={t("settings.iban")}>
                <TextInput value={bank.iban} onChange={(v) => { onChange({ ...bank, iban: v }); setIbanError(null); onIbanError?.(false); }} onBlur={handleIbanBlur} placeholder="DE00 0000 0000 0000 0000 00" />
            </SettingRow>
            {ibanError && (
                <div className="settings-info-banner settings-info-banner--error">
                    <TbAlertCircle />
                    {ibanError}
                </div>
            )}
            <SettingRow
                title={t("settings.bic")}
                description={bicLookingUp ? t("settings.bicLookingUp") : undefined}
            >
                <TextInput value={bank.bic} onChange={(v) => onChange({ ...bank, bic: v })} placeholder="XXXXXXXX" />
            </SettingRow>
            <SettingRow title={t("settings.taxNumber")}>
                <TextInput value={bank.taxNumber} onChange={(v) => onChange({ ...bank, taxNumber: v })} placeholder="00/000/00000" />
            </SettingRow>
            <SettingRow title={t("settings.scanPayName")} description={t("settings.scanPayNameDesc")}>
                <TextInput
                    value={bank.scanPayName}
                    onChange={(v) => onChange({ ...bank, scanPayName: v })}
                    placeholder={t("settings.scanPayNamePlaceholder")}
                />
            </SettingRow>
        </Section>
    );
}
